import { withFieldGroup } from '@massilia/features'
import { cn, Label, RadioGroup, RadioGroupItem } from '@massilia/ui'
import { format } from 'date-fns'
import { fr } from 'date-fns/locale'

import {
  AGENCY_LOCATION_LABEL,
  AGENCY_OPENING_HOURS_LABEL,
  AGENCY_PHONE,
} from '@/lib/constants/agency'

import type { CheckoutFormValues, PaymentMethodFormValue } from '../checkout-form.schema'

import { paymentMethods } from '../checkout-form.schema'
import { BankTransferDetails } from './bank-transfer-details'

interface PaymentMethodSelectorProps {
  installmentPaymentDeadline?: string
}

function isPaymentMethod(value: string): value is PaymentMethodFormValue {
  return paymentMethods.includes(value as PaymentMethodFormValue)
}

export const PaymentMethodSelector = withFieldGroup({
  defaultValues: {} as Pick<CheckoutFormValues, 'paymentMethod'>,
  props: {} as PaymentMethodSelectorProps,
  render: function PaymentMethodFields({ group, installmentPaymentDeadline }) {
    const deadlineFormatted = installmentPaymentDeadline
      ? format(installmentPaymentDeadline, 'dd MMMM yyyy', { locale: fr })
      : null

    return (
      <div className="flex w-full flex-col gap-4">
        <div className="text-gray-light-900 font-semibold">Quel est votre moyen de paiement ?</div>
        <group.AppField name="paymentMethod">
          {(field) => {
            const selected = field.state.value

            return (
              <RadioGroup
                className="flex flex-col gap-0"
                onValueChange={(value) => {
                  if (isPaymentMethod(value)) {
                    field.handleChange(value)
                  }
                }}
                value={selected}
              >
                {/* Carte bancaire */}
                <PaymentMethodOption
                  description={
                    <p className="text-gray-light-700 text-sm">
                      Vous serez redirigé vers notre plateforme de paiement sécurisée pour saisir
                      vos informations de carte bancaire.
                    </p>
                  }
                  id="credit-card"
                  isFirst
                  isSelected={selected === 'credit-card'}
                  label="Carte bancaire"
                  nextIsSelected={selected === 'floa'}
                  value="credit-card"
                />

                {/* Floa */}
                <PaymentMethodOption
                  description={
                    <p className="text-gray-light-700 text-sm">
                      Contactez l&apos;agence pour mettre en place le paiement en 3 ou 4 fois via
                      Floa.
                    </p>
                  }
                  id="floa"
                  isSelected={selected === 'floa'}
                  label="Floa 3/4x avec frais"
                  prevIsSelected={selected === 'credit-card'}
                  nextIsSelected={selected === 'vacation-cheque'}
                  value="floa"
                />

                {/* Chèque vacances */}
                <PaymentMethodOption
                  description={
                    <div className="flex w-full flex-col gap-4">
                      {deadlineFormatted && (
                        <div className="flex items-center justify-between">
                          <div className="text-gray-light-700 text-sm font-medium">
                            Date limite de réception des chèques vacances
                          </div>
                          <div className="text-gray-light-900 text-sm font-semibold">
                            {deadlineFormatted}
                          </div>
                        </div>
                      )}
                      <div className="flex flex-col">
                        <div className="text-gray-light-900 text-sm font-semibold">
                          Nous les faire parvenir par courrier en recommandé à l&apos;agence.
                        </div>
                        <div className="text-gray-light-900 text-sm">
                          Nous vous informerons des réception du courrier.
                        </div>
                      </div>
                      <div className="text-gray-light-900 text-sm font-semibold">
                        {AGENCY_LOCATION_LABEL}
                      </div>
                      <div className="text-gray-light-900 text-sm">
                        {AGENCY_OPENING_HOURS_LABEL}
                      </div>
                      <div className="text-gray-light-900 text-sm">Téléphone : {AGENCY_PHONE}</div>
                    </div>
                  }
                  id="vacation-cheque"
                  isSelected={selected === 'vacation-cheque'}
                  label="Chèque vacances"
                  prevIsSelected={selected === 'floa'}
                  nextIsSelected={selected === 'vacation-e-cheque'}
                  value="vacation-cheque"
                />

                {/* E-chèque vacances */}
                <PaymentMethodOption
                  description={
                    <div className="flex w-full flex-col gap-4">
                      {deadlineFormatted && (
                        <div className="flex items-center justify-between">
                          <div className="text-gray-light-700 text-sm font-medium">
                            Date limite de réception des E-chèques vacances
                          </div>
                          <div className="text-gray-light-900 text-sm font-semibold">
                            {deadlineFormatted}
                          </div>
                        </div>
                      )}
                      <div className="text-gray-light-900 text-sm font-semibold">
                        Appelez l&apos;agence aux horaires d&apos;ouverture.
                      </div>
                      <div className="text-gray-light-900 text-sm">
                        {AGENCY_OPENING_HOURS_LABEL}
                      </div>
                      <div className="text-gray-light-900 text-sm">Téléphone : {AGENCY_PHONE}</div>
                    </div>
                  }
                  id="vacation-e-cheque"
                  isSelected={selected === 'vacation-e-cheque'}
                  label="E-chèque vacances"
                  prevIsSelected={selected === 'vacation-cheque'}
                  nextIsSelected={selected === 'bank-transfer'}
                  value="vacation-e-cheque"
                />

                {/* Virement bancaire */}
                <PaymentMethodOption
                  description={
                    <BankTransferDetails installmentPaymentDeadline={installmentPaymentDeadline} />
                  }
                  id="bank-transfer"
                  isSelected={selected === 'bank-transfer'}
                  label="Virement bancaire"
                  prevIsSelected={selected === 'vacation-e-cheque'}
                  nextIsSelected={selected === 'cash'}
                  value="bank-transfer"
                />

                {/* Espèces */}
                <PaymentMethodOption
                  description={
                    <div className="flex w-full flex-col gap-3">
                      <div className="text-gray-light-900 text-sm font-semibold">
                        {AGENCY_LOCATION_LABEL}
                      </div>
                      <div className="text-gray-light-900 text-sm">
                        {AGENCY_OPENING_HOURS_LABEL}
                      </div>
                      <div className="text-gray-light-900 text-sm">Téléphone : {AGENCY_PHONE}</div>
                    </div>
                  }
                  id="cash"
                  isLast
                  isSelected={selected === 'cash'}
                  label="Espèces (uniquement en agence)"
                  prevIsSelected={selected === 'bank-transfer'}
                  value="cash"
                />
              </RadioGroup>
            )
          }}
        </group.AppField>
      </div>
    )
  },
})

interface PaymentMethodOptionProps {
  id: string
  description: React.ReactNode
  isFirst?: boolean
  isLast?: boolean
  isSelected: boolean
  label: string
  nextIsSelected?: boolean
  prevIsSelected?: boolean
  value: string
}

function PaymentMethodOption({
  id,
  description,
  isFirst = false,
  isLast = false,
  isSelected,
  label,
  nextIsSelected = false,
  prevIsSelected = false,
  value,
}: PaymentMethodOptionProps) {
  return (
    <div
      className={cn(
        'border-gray-light-300 border-r border-l transition-all',
        isFirst && 'rounded-t-2xl border-t',
        isLast && 'rounded-b-2xl border-b',
        isSelected && 'border-brand-600 bg-brand-50 border-t border-b',
        !isSelected && prevIsSelected && 'border-t',
        !isSelected && nextIsSelected && 'border-b',
        !isFirst && !isSelected && !prevIsSelected && 'border-t',
        !isLast && !isSelected && !nextIsSelected && 'border-b'
      )}
    >
      <div className="flex cursor-pointer items-start gap-2 p-4">
        <RadioGroupItem className="mt-0.5" id={id} value={value} />
        <Label className="flex-1 cursor-pointer text-sm font-semibold" htmlFor={id}>
          {label}
        </Label>
      </div>
      <div
        className={cn(
          'overflow-hidden transition-all duration-300 ease-in-out',
          isSelected ? 'max-h-96 opacity-100' : 'pointer-events-none max-h-0 opacity-0'
        )}
      >
        <div className="px-4 pb-4">{description}</div>
      </div>
    </div>
  )
}
